import React from 'react';

import styled from 'styled-components';

import AiCiSectionIcon from 'components/AiCompetitiveIntelligence/AiCiSectionIcon';
import AlertActionPlanCard from 'components/Alerts/AlertActionPlanCard';
import { ActionPlanListItemType } from 'types/actionPlans';

import {
  DashboardCard,
  EmptySection,
  SectionDescription,
  SectionHeading,
  SectionTitle,
} from './SpeedToLeadSection.styles';

const ActionPlanList = styled.div`
  border-left: 3px solid var(--lavender-purple);
  padding: 14px;
  background: var(--white);
`;

const ActionPlanTable = styled.div`
  padding: 10px 14px;
  background: var(--background);
`;

const ActionPlanHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 360px);
  gap: 24px;
  padding: 0 10px 8px;
  color: var(--text-secondary);
  font-family: var(--font-bold);
  font-size: 10px;
  text-transform: uppercase;

  @media (max-width: 700px) {
    display: none;
  }
`;

const ActionPlanRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 360px);
  align-items: center;
  gap: 24px;
  padding: 10px;
  border-bottom: 1px solid var(--line-gray);

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const ActionPlanObjective = styled.div`
  min-width: 0;
`;

const ActionPlanObjectiveName = styled.div`
  color: var(--text-primary);
  font-family: var(--font-bold);
  font-size: 14px;
`;

const ActionPlanObjectiveDescription = styled.div`
  margin-top: 2px;
  color: var(--text-secondary);
  font-size: 12px;
`;

const ActionPlanButton = styled.button`
  width: 100%;
  min-width: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }

  > div {
    width: 100%;
    background: var(--white);
  }
`;

const DESCRIPTION_LABEL = {
  SIGNAL: 'Signal:',
  DATA: 'Data:',
  IMPACT: 'Impact:',
} as const;

const parseObjective = (plan: ActionPlanListItemType) => {
  const description = plan.description || '';
  const sections = description.split('\n').reduce<Record<string, string>>((result, line) => {
    const label = Object.values(DESCRIPTION_LABEL).find((candidate) => line.startsWith(candidate));
    if (label) {
      result[label] = line.slice(label.length).trim();
    }
    return result;
  }, {});

  return {
    name: sections[DESCRIPTION_LABEL.SIGNAL] || plan.name,
    description: sections[DESCRIPTION_LABEL.IMPACT] || sections[DESCRIPTION_LABEL.DATA] || description,
  };
};

type Props = {
  actionPlanStatus: 'pending' | 'completed' | 'failed' | 'skipped';
  actionPlans: ActionPlanListItemType[];
  actionPlansEnabled: boolean;
  actionPlansError: boolean;
  onActionPlanOpen: (planId: number) => void;
};

const SpeedToLeadActionPlans = ({
  actionPlans,
  actionPlansEnabled,
  actionPlansError,
  actionPlanStatus,
  onActionPlanOpen,
}: Props) => (
  <DashboardCard $muted>
    <SectionHeading>
      <AiCiSectionIcon src="/icons/checklist-white.svg" variant="action" />
      <div>
        <SectionTitle>Resulting Action Plans</SectionTitle>
        <SectionDescription>AI-generated action plans for what to train next.</SectionDescription>
      </div>
    </SectionHeading>
    {actionPlansError ? (
      <EmptySection>Failed to load Speed to Lead action plans. Please try again.</EmptySection>
    ) : actionPlanStatus === 'failed' ? (
      <EmptySection>Speed to Lead action plans could not be generated. Please regenerate the analysis.</EmptySection>
    ) : actionPlanStatus === 'pending' ? (
      <EmptySection>Speed to Lead action plans are still being generated.</EmptySection>
    ) : actionPlans.length === 0 ? (
      <EmptySection>No Speed to Lead action plans were generated for this community.</EmptySection>
    ) : (
      <ActionPlanList>
        <ActionPlanTable>
          <ActionPlanHeader>
            <div>Objective</div>
            <div>Action Plan</div>
          </ActionPlanHeader>
          {actionPlans.map((plan) => {
            const objective = parseObjective(plan);
            return (
              <ActionPlanRow key={plan.id}>
                <ActionPlanObjective>
                  <ActionPlanObjectiveName>{objective.name}</ActionPlanObjectiveName>
                  {objective.description && (
                    <ActionPlanObjectiveDescription>{objective.description}</ActionPlanObjectiveDescription>
                  )}
                </ActionPlanObjective>
                <ActionPlanButton
                  type="button"
                  disabled={!actionPlansEnabled}
                  onClick={() => onActionPlanOpen(plan.id)}
                >
                  <AlertActionPlanCard actionPlanName={plan.name} disabled={!actionPlansEnabled} compact />
                </ActionPlanButton>
              </ActionPlanRow>
            );
          })}
        </ActionPlanTable>
      </ActionPlanList>
    )}
  </DashboardCard>
);

export default SpeedToLeadActionPlans;
